/**
 * Validação de áudio no navegador. Puro: sem Vue, sem Firebase.
 *
 * Espelha o imageFile, com duas diferenças que valem entender.
 *
 * Não há conversão: transcodificar áudio no navegador exigiria uma biblioteca
 * pesada, então o arquivo sobe como veio e o teto de origem é o de armazenamento.
 *
 * E os magic bytes não bastam. Eles identificam CONTÊINER, não codec: `ftyp` é
 * .m4a mas também .mp4 e .mov; `OggS` é Vorbis/Opus mas também Theora, que é
 * vídeo; Matroska serve WebM de áudio e de vídeo. Um arquivo de vídeo passaria
 * pela peneira. Por isso, depois deles vem a sonda de decodificação, que é o papel
 * que o createImageBitmap cumpre do lado da imagem — e que de quebra dá a duração.
 */

export const MAX_BYTES = 10 * 1024 * 1024
/** A sonda trava se o arquivo for estranho o bastante; não vale prender o campo. */
const PROBE_TIMEOUT_MS = 8000

export const ACCEPTED_TYPES = [
    'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/flac', 'audio/webm'
]
/** Extensões junto do MIME: navegador nenhum concorda sobre o tipo de .m4a e .flac. */
export const ACCEPT_ATTRIBUTE = [...ACCEPTED_TYPES, '.mp3', '.wav', '.ogg', '.m4a', '.flac'].join(',')

export type AudioErrorCode = 'audio-type' | 'audio-too-large' | 'audio-decode'

export type PrepareAudioResult =
    | { ok: true; blob: Blob; duration: number }
    | { ok: false; code: AudioErrorCode }

/**
 * Tipo real pelos primeiros bytes. Devolve o MIME, que é usado para recarimbar o
 * Blob: o File.type vem do sistema operacional e no Windows costuma vir vazio —
 * sem isto o objeto iria para o Storage como application/octet-stream e o Safari
 * se recusaria a tocar.
 */
async function sniffType(file: Blob): Promise<string | null> {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer())
    const is = (offset: number, ...bytes: number[]) =>
        bytes.every((byte, i) => head[offset + i] === byte)

    //ID3 na frente, ou o sync de um quadro MPEG solto
    if (is(0, 0x49, 0x44, 0x33)) return 'audio/mpeg'
    if (head[0] === 0xff && (head[1] & 0xe0) === 0xe0) return 'audio/mpeg'
    //RIFF....WAVE
    if (is(0, 0x52, 0x49, 0x46, 0x46) && is(8, 0x57, 0x41, 0x56, 0x45)) return 'audio/wav'
    if (is(0, 0x4f, 0x67, 0x67, 0x53)) return 'audio/ogg'
    if (is(0, 0x66, 0x4c, 0x61, 0x43)) return 'audio/flac'
    //....ftyp — contêiner MP4; a marca vem logo depois e não distingue áudio de vídeo
    if (is(4, 0x66, 0x74, 0x79, 0x70)) return 'audio/mp4'
    if (is(0, 0x1a, 0x45, 0xdf, 0xa3)) return 'audio/webm'
    return null
}

/**
 * Prova que o navegador decodifica, e devolve a duração. Um arquivo de vídeo com
 * contêiner compatível chega até aqui; ele até carrega metadados, mas o que
 * importa é que isto é o único ponto em que o navegador de fato abre o arquivo.
 */
function probe(url: string): Promise<number> {
    return new Promise<number>((resolve, reject) => {
        const audio = new Audio()
        let done = false
        const finish = (fn: () => void) => {
            if (done) return
            done = true
            clearTimeout(timer)
            audio.removeAttribute('src')
            fn()
        }
        const timer = setTimeout(() => finish(() => reject(new Error('timeout'))), PROBE_TIMEOUT_MS)
        audio.addEventListener('loadedmetadata', () => finish(() => resolve(audio.duration)))
        audio.addEventListener('error', () => finish(() => reject(new Error('decode'))))
        audio.preload = 'metadata'
        audio.src = url
    })
}

/** Valida e devolve o Blob a armazenar, com o MIME correto e a duração. */
export async function prepareAudio(file: File): Promise<PrepareAudioResult> {
    if (file.size > MAX_BYTES) return {ok: false, code: 'audio-too-large'}

    const type = await sniffType(file)
    if (!type) return {ok: false, code: 'audio-type'}

    //O recarimbo é o ponto crítico: a partir daqui o Blob leva o MIME farejado, e é
    //ele que vira o contentType do objeto no Storage.
    const blob = file.slice(0, file.size, type)

    const url = URL.createObjectURL(blob)
    try {
        const duration = await probe(url)
        if (!isFinite(duration) || duration <= 0) return {ok: false, code: 'audio-decode'}
        return {ok: true, blob, duration}
    } catch (err) {
        return {ok: false, code: 'audio-decode'}
    } finally {
        URL.revokeObjectURL(url)
    }
}

/** Duração em m:ss, para o campo mostrar algo além de um botão. */
export function formatDuration(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return ''
    const total = Math.round(seconds)
    const minutes = Math.floor(total / 60)
    return `${minutes}:${String(total % 60).padStart(2, '0')}`
}

export default {prepareAudio, formatDuration, ACCEPT_ATTRIBUTE, MAX_BYTES}
